import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AuthContext } from '../Auth/AuthContext';
import useCachedSettingsOptimized from '../../hooks/useCachedSettingsOptimized';

const CompanySettingsContext = createContext();

export const useCompanySettingsContext = () => {
  const context = useContext(CompanySettingsContext);
  if (!context) {
    throw new Error('useCompanySettingsContext must be used within a CompanySettingsProvider');
  }
  return context;
};

export const CompanySettingsProvider = ({ children }) => {
  const authContext = useContext(AuthContext);
  const { user } = authContext || {};
  
  // ✅ Usar hook otimizado que elimina loops infinitos
  const {
    settings,
    loading,
    loaded,
    error,
    getSetting: getCachedSetting,
    updateSetting: updateCachedSetting,
    invalidateAndReload,
    hasSettings,
    companyId
  } = useCachedSettingsOptimized();

  // ✅ SOLUÇÃO: Usar useRef para estabilizar getSetting e evitar re-criação
  const getSettingRef = useRef();
  
  // ✅ SOLUÇÃO: Estabilizar função getSetting usando useMemo baseado apenas nos settings
  const getSetting = useMemo(() => {
    const stableGetSetting = (key, defaultValue = null) => {
      const value = settings[key];
      return value !== undefined ? value : defaultValue;
    };
    getSettingRef.current = stableGetSetting;
    return stableGetSetting;
  }, [settings]); // ← Dependência apenas de settings, não de getCachedSetting

  // ✅ SOLUÇÃO: Estabilizar updateSetting usando useMemo
  const updateSetting = useMemo(() => {
    return (key, value) => {
      updateCachedSetting(key, value);
    };
  }, [updateCachedSetting]);

  // ✅ SOLUÇÃO: Estabilizar reloadSettings usando useMemo
  const reloadSettings = useMemo(() => {
    return () => {
      return invalidateAndReload();
    };
  }, [invalidateAndReload]);

  // ✅ SOLUÇÃO: Memoizar value do context para evitar re-renders desnecessários
  const value = useMemo(() => ({
    settings,
    loading,
    loaded,
    error,
    getSetting,
    updateSetting,
    reloadSettings,
    // ✅ Propriedades adicionais do hook otimizado
    hasSettings,
    companyId
  }), [settings, loading, loaded, error, getSetting, updateSetting, reloadSettings, hasSettings, companyId]);

  return (
    <CompanySettingsContext.Provider value={value}>
      {children}
    </CompanySettingsContext.Provider>
  );
};

export default CompanySettingsContext;